/**
 * Nuxtのruntimeのpublic configから取得した値をbooleanに正規化する。
 *
 * env由来の値は文字列として渡るため、`Boolean(value)` では文字列 `'false'` も
 * truthy 扱いになってしまう。明示的に `true` または `'true'` のみを true とし、
 * それ以外（`'false'`、空文字、undefined 等）は false として扱う。
 *
 * @param value runtimeConfig.public 経由で取得した値
 * @returns true: 明示的に true 指定、false: それ以外
 */
export const parsePublicBoolean = (value: unknown): boolean => {
  return value === true || value === 'true';
};

/**
 * 数値設定値のパースオプション
 */
export type NumberConfigOptions = {
  /** 不正値・範囲外値の場合に返す既定値 */
  defaultValue: number;
  /** 許容する下限値（指定時、parsed < min なら defaultValue を返す） */
  min?: number;
  /** 許容する上限値（指定時、parsed > max なら defaultValue を返す） */
  max?: number;
};

/**
 * 文字列の数値設定値をパースして number を返す。
 *
 * システム設定APIから取得した数値文字列をパースする用途を想定。
 * 数値でない・min/max 範囲外の場合は defaultValue を返す。
 *
 * @param value 数値文字列（未取得時はundefined）
 * @param options デフォルト値と許容範囲
 * @returns パース結果の数値、または defaultValue
 */
export const parseNumberConfig = (
  value: string | undefined,
  options: NumberConfigOptions,
): number => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return options.defaultValue;
  }

  const parsed = Number(trimmedValue);
  if (!Number.isFinite(parsed)) {
    return options.defaultValue;
  }
  if (options.min !== undefined && parsed < options.min) {
    return options.defaultValue;
  }
  if (options.max !== undefined && parsed > options.max) {
    return options.defaultValue;
  }
  return parsed;
};
